import type { EncounterScrubberResult } from './types';

const ADD_OR_KEEP_ACTIONS = new Set<EncounterScrubberResult['action']>([
  'keep',
  'suggest_replace',
  'suggest_add',
]);

function providerHasAccess(code: string, allowed: string[]): boolean {
  if (allowed.length === 0) return true;
  return allowed.includes(code);
}

function billingReviewMessage(code: string, context: string): string {
  return `${code} is not available to this provider. ${context} Billing review is needed.`;
}

/**
 * Global post-processing: any keep/add/replace recommendation must pass provider CPT access.
 */
export function applyProviderCptAccessGuard(
  results: EncounterScrubberResult[],
  providerAllowedCptCodes: string[],
): EncounterScrubberResult[] {
  if (providerAllowedCptCodes.length === 0) return results;

  return results.map((result) => {
    const codesToCheck: string[] = [];
    if (ADD_OR_KEEP_ACTIONS.has(result.action)) {
      codesToCheck.push(result.code);
      if (result.suggestedCode) codesToCheck.push(result.suggestedCode);
      if (result.suggestedCodes) codesToCheck.push(...result.suggestedCodes);
    }

    const denied = codesToCheck.filter((c) => !providerHasAccess(c, providerAllowedCptCodes));
    if (denied.length === 0) return result;

    const deniedCode = denied[0];
    const wasKeep = result.action === 'keep';
    return {
      ...result,
      action: 'billing_review',
      severity: 'warning',
      message: billingReviewMessage(
        deniedCode,
        wasKeep
          ? `Selected code ${result.code} cannot be kept.`
          : `Suggested code ${deniedCode} cannot be recommended.`,
      ),
      suggestedCode: undefined,
      suggestedCodes: undefined,
    };
  });
}
